import type { FC } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Divider, Typography } from '@mui/material';

import { SECTION } from 'src/constants/section';
import type { DetailedFollowUpTicket } from 'src/types/followUp';
import { useUserContext } from 'src/components/app/context/UserContext';
import useUserPermissions from 'src/components/app/context/UserContext/useUserPermissions';
import BaseSnackbar from 'src/components/common/Snackbar';
import SuccessSnackbar from 'src/components/common/Snackbar/SuccessSnackbar';
import StatusPill from 'src/components/followUp/TicketOverviewPage/TicketTable/Cells/StatusPill';

import SidebarSection from './SidebarSection';
import TicketDataSection from './TicketDataSection';
import useTicketStatusUpdate from './useTicketStatusUpdate';

import { styles } from '../ticketDetail.styles';

export const TICKET_DETAIL_SIDEBAR_DATA_TEST_ID = 'ticket-detail-sidebar';
export const REOPEN_TICKET_BUTTON_DATA_TEST_ID = 'reopen-ticket-button';
export const CLOSE_TICKET_BUTTON_DATA_TEST_ID = 'close-ticket-button';

const ModalSidebar: FC<{
  ticket: DetailedFollowUpTicket;
  loading: boolean;
  refetchList: () => void;
}> = ({ ticket, loading, refetchList }) => {
  const { user } = useUserContext();
  const isFollowUpOwner =
    !!user && !!ticket && ticket.followUpOwner === user.id;
  const {
    hasReadOnlyPermissionForModuleType,
    hasAdminPermissionForModuleType,
  } = useUserPermissions();
  const readOnly =
    hasReadOnlyPermissionForModuleType(SECTION.FOLLOW_UP, ticket?.surveyId) &&
    !hasAdminPermissionForModuleType(SECTION.FOLLOW_UP);
  const { t } = useTranslation('followUp');
  const { reopenTicket, snackbar, setSnackbar, closeTicket, isUpdating } =
    useTicketStatusUpdate({
      ticket,
      refetchList,
    });

  if (!ticket) return null;

  return (
    <Box
      sx={{ width: '30%', ...styles.scrollContainer }}
      data-test-id={TICKET_DETAIL_SIDEBAR_DATA_TEST_ID}
    >
      <SidebarSection>
        <Typography component="h3" variant="h5">
          {t('ticketDetails')}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Typography>{t('tableHeader.status')}</Typography>
          <StatusPill status={ticket.status} />
        </Box>

        {ticket.status === 'Closed' ? (
          <Button
            fullWidth
            onClick={() => reopenTicket()}
            disabled={loading || isUpdating || (readOnly && !isFollowUpOwner)}
            data-test-id={REOPEN_TICKET_BUTTON_DATA_TEST_ID}
          >
            {t('markOpen')}
          </Button>
        ) : (
          <Button
            fullWidth
            onClick={() => closeTicket()}
            disabled={loading || isUpdating || (readOnly && !isFollowUpOwner)}
            data-test-id={CLOSE_TICKET_BUTTON_DATA_TEST_ID}
          >
            {t('markClosed')}
          </Button>
        )}
      </SidebarSection>

      <Divider />

      <TicketDataSection ticket={ticket} refetchList={refetchList} />

      <SuccessSnackbar
        open={!!snackbar?.success}
        onClose={() => setSnackbar(null)}
        title={snackbar?.title}
        message={snackbar?.message}
      />
      <BaseSnackbar
        open={!!snackbar && !snackbar.success}
        onClose={() => setSnackbar(null)}
        title={snackbar?.title}
        message={snackbar?.message}
      />
    </Box>
  );
};

export default ModalSidebar;
